import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import useEmblaCarousel from 'embla-carousel-react'
import { IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from 'react-icons/io'
import testimonials from '../data/testimonials.json'

const TestimonialsCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, skipSnaps: false, duration: 50 })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const onSelect = () => {
    if (!emblaApi) return
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi])

  // Auto-swipe every 3 seconds, paused on hover or when window is hidden
  useEffect(() => {
    if (!emblaApi || isHovered) return

    let interval

    const startAutoScroll = () => {
      if (interval) clearInterval(interval)
      interval = setInterval(() => {
        emblaApi.scrollNext()
      }, 3000)
    }

    const stopAutoScroll = () => {
      if (interval) clearInterval(interval)
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAutoScroll()
      } else {
        startAutoScroll()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    if (!document.hidden) {
      startAutoScroll()
    }

    return () => {
      stopAutoScroll()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [emblaApi, isHovered])

  const scrollPrev = () => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }

  const scrollNext = () => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }

  return (
    <Container onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <NavButtonLeft onClick={scrollPrev}>
        <IoIosArrowDropleftCircle size={28} />
      </NavButtonLeft>

      <EmblaViewport ref={emblaRef}>
        <EmblaContainer>
          {testimonials.map(testimonial => (
            <EmblaSlide key={testimonial.id}>
              <Quote>{testimonial.quote}</Quote>
              <Name>{testimonial.name}</Name>
              <Role>{testimonial.role}</Role>
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaViewport>

      <NavButtonRight onClick={scrollNext}>
        <IoIosArrowDroprightCircle size={28} />
      </NavButtonRight>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  margin: 0;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 280px;
  max-height: 280px;
  box-sizing: border-box;
  position: relative;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 120px,
    black calc(100% - 120px),
    transparent 100%
  );
`

const EmblaViewport = styled.div`
  overflow: hidden;
  flex: 1;
  height: 100%;
`

const EmblaContainer = styled.div`
  display: flex;
  gap: 0;
  height: 100%;
  margin: 0;
  padding: 0;
`

const EmblaSlide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 2rem;
  text-align: center;
  height: 100%;
`

const Quote = styled.p`
  font-size: 0.8rem;
  font-style: italic;
  color: #333;
  margin: 0 0 0.3rem 0;
  line-height: 1.4;
  max-width: 600px;
`

const Name = styled.div`
  font-size: 0.65rem;
  color: #8cc63f;
  font-weight: 600;
  margin: 0.15rem 0 0.08rem 0;
`

const Role = styled.div`
  font-size: 0.55rem;
  color: #666;
`

const NavButtonLeft = styled.button`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 15%;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 1rem;
  transition: color 0.2s ease;
  z-index: 10;

  &:hover {
    color: #666;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const NavButtonRight = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 15%;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 1rem;
  transition: color 0.2s ease;
  z-index: 10;

  &:hover {
    color: #666;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

export default TestimonialsCarousel
